import React, { useEffect } from 'react';
import {
  Switch, Route, useParams, useRouteMatch,
} from 'react-router';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { isMobileOnly } from 'react-device-detect';
import { ViewFrame, UploadPanel } from '../../components';
import { useTitle } from '../../hooks';
import Overview from './tab-overview/Overview';
import Scripts from './tab-scripts/Scripts';
import Admin from './tab-admin/Admin';
import Media from './tab-media/Media';
import Activity from './tab-activity/Activity';
import { selectProject } from '../../store/modules/projects/selectors';
import { getProjectRequest, getProjectRefreshRequest } from '../../store/modules/projects/actions';
import { setRefreshArticleList } from '../../store/modules/articles/actions';
import { getScenesListRequest, initScenesRequest, setRefreshScenesList } from '../../store/modules/scenes/actions';
import GroupActions from './actions/GroupActions';
import ListenChangeRouteOnUpload from './ListenChangeRouteOnUpload';
import { useUploadFiles, UploadContext } from './tab-media/useUploadFiles';
import { useSSE, SseContext } from '../../services/SSE';
import hotKeyConverter from '../../utility/keyCodeConverter';
import RouteGetter from '../../routes/RouteGetter';
import TabRedirect from '../../components/tab-redirect/TabRedirect';
import SideTabs from '../../components/side-tabs/SideTabs';
import useStyles from './styles';
import { getQueryFilter } from '../../utility/helpers';
import { selectFormScenes, selectListScenes } from '../../store/modules/scenes/selectors';
import Orders from './tab-orders/ProjectOrdersRoute';
import CancelVideoEditOrderButton from './actions/ProjectVideoEditOrder/CancelVideoEditOrderButton';
import ProjectStatusGroupActions from './actions/ProjectStatusGroupActions/ProjectStatusGroupActions';

const Project = () => {
  const dispatch = useDispatch();
  const { data: project, loading, error } = useSelector(selectProject);
  const files = useUploadFiles();
  const sse = useSSE();
  const { id: projectId } = useParams<{ id: string }>();
  const title = project?.title || 'New Project';
  const { path } = useRouteMatch();
  const classes = useStyles();
  const mobileClasses = isMobileOnly ? classes.overflowHidden : '';
  useTitle(title);
  const scenesList = useSelector(selectListScenes);
  const formScenes = useSelector(selectFormScenes);

  useEffect(() => {
    const currentProjectId = parseInt(projectId, 10);

    if (currentProjectId) {
      const query = {
        filter: {
          project_id: currentProjectId,
        },
      };

      if (!scenesList.data && !scenesList.loading) {
        dispatch(getScenesListRequest(query));
      }

      if (scenesList.data && !scenesList.loading) {
        const filter = getQueryFilter('project_id', scenesList.data.filters.active);
        if (filter !== undefined) {
          if (Number(filter.value) !== currentProjectId) {
            dispatch(getScenesListRequest(query));
          }
        }
      }

      if (!scenesList.loading && scenesList.dirty) {
        dispatch(getScenesListRequest(query));
      }

      if (!formScenes.data && !formScenes.loading) {
        dispatch(initScenesRequest());
      }
    }
  }, [dispatch, projectId, scenesList, formScenes]);

  useEffect(() => {
    const currentProjectId = parseInt(projectId, 10);

    if (currentProjectId && !loading && !error) {
      if (project) {
        sse.addSource({
          url: project.ref.sse_topic,
          onMessage: (data) => {
            if (data.action === 'updated') {
              dispatch(getProjectRefreshRequest(currentProjectId));
            }
            if (data.action === 'child_structure_updated' || data.action === 'child_content_updated') {
              if (path.includes('/media')) {
                dispatch(setRefreshArticleList(true));
                dispatch(setRefreshScenesList(true));
              }
            }
          },
        });

        if (project.id !== currentProjectId) {
          dispatch(getProjectRequest(currentProjectId));
        }
      } else {
        dispatch(getProjectRequest(currentProjectId));
      }
    }
  }, [dispatch, project, projectId, sse, path, loading, error]);

  useEffect(() => {
    const hotKeyList = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.keyCode === hotKeyConverter('l')) {
        event.preventDefault();
        dispatch(push(RouteGetter.get('project').index));
      }
    };
    window.addEventListener('keydown', hotKeyList);

    return () => {
      window.removeEventListener('keydown', hotKeyList);
    };
  }, [dispatch]);

  return (
    <SseContext.Provider value={sse}>
      <UploadContext.Provider value={files}>
        <ListenChangeRouteOnUpload />
        <TabRedirect />
        <ViewFrame
          loading={loading}
          title={title}
          backToListLink
          className={mobileClasses}
          actionPanel={(project && project.id)
          && (
            <div className={classes.controlPanel}>
              <ProjectStatusGroupActions project={project} />
              <GroupActions project={project} />
              <CancelVideoEditOrderButton />
            </div>
          )}
        >
          <Grid container spacing={1}>
            {
              !loading
              && (
                <Grid item lg={2} xs={12}>
                  <SideTabs variant="projects" />
                </Grid>
              )
            }
            <Grid item xs={12} lg={10}>
              <Switch>
                <Route>
                  <Overview />
                  <Scripts />
                  <Media />
                  <Activity />
                  <Orders />
                  <Admin />
                </Route>
              </Switch>
            </Grid>
            <UploadPanel />
          </Grid>
        </ViewFrame>
      </UploadContext.Provider>
    </SseContext.Provider>
  );
};

export default Project;
